/**
 * User Service
 *
 * Type-safe user management service using @monorepo/shared schemas
 */

import {
  type PaginatedUsersResponse,
  PaginatedUsersResponseSchema,
  type UpdateUser,
  type UpdateUserPreferences,
  UpdateUserPreferencesSchema,
  UpdateUserSchema,
  type UserQuery,
  type UserResponse,
  UserResponseSchema,
} from '@monorepo/shared/schemas/users'
import { get, put } from '@/lib/api-client'

export const userService = {
  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<UserResponse> {
    return get('/users/profile', UserResponseSchema)
  },

  /**
   * Get user by ID
   */
  async getUser(id: string): Promise<UserResponse> {
    return get(`/users/${id}`, UserResponseSchema)
  },

  /**
   * List users (admin only)
   */
  async getUsers(query?: UserQuery): Promise<PaginatedUsersResponse> {
    const queryParams = new URLSearchParams()

    if (query?.search) queryParams.append('search', query.search)
    if (query?.tenant_id) queryParams.append('tenant_id', query.tenant_id)
    if (query?.role) queryParams.append('role', query.role)
    if (query?.email_verified !== undefined)
      queryParams.append('email_verified', query.email_verified.toString())
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())
    if (query?.sort) queryParams.append('sort', query.sort)
    if (query?.order) queryParams.append('order', query.order)

    const url = `/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedUsersResponseSchema)
  },

  /**
   * Update current user profile
   */
  async updateProfile(data: UpdateUser): Promise<UserResponse> {
    const validated = UpdateUserSchema.parse(data)

    return put('/users/profile', validated, UserResponseSchema)
  },

  /**
   * Update user preferences
   */
  async updatePreferences(data: UpdateUserPreferences): Promise<UserResponse> {
    const validated = UpdateUserPreferencesSchema.parse(data)

    return put('/users/profile/preferences', validated, UserResponseSchema)
  },

  /**
   * Upload user avatar
   */
  async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData()
    formData.append('avatar', file)

    // This uses fetch directly as it needs multipart/form-data
    const token = localStorage.getItem('access_token')
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'}/users/profile/avatar`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    )

    if (!response.ok) {
      throw new Error('Failed to upload avatar')
    }

    return response.json()
  },
}
